import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  ClipboardList,
  FileText,
  Handshake,
  Home,
  MapPin,
  MessageSquare,
  Scale,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import "../../styles/Features.css";

const pillars = [
  {
    icon: Users,
    title: "Accompagnement citoyen",
    text: "Un premier cadre pour exprimer le problème, comprendre les étapes et ne pas avancer seul.",
  },
  {
    icon: Handshake,
    title: "Médiation",
    text: "Une approche de dialogue pour désamorcer les tensions et chercher une issue constructive.",
  },
  {
    icon: Home,
    title: "Aide au logement",
    text: "Des repères pour les situations liées au logement, aux travaux, à l'insalubrité ou aux échanges avec un bailleur.",
  },
  {
    icon: ClipboardList,
    title: "Assistance administrative",
    text: "Une aide pour organiser les pièces, clarifier les demandes et préparer les démarches essentielles.",
  },
  {
    icon: Bot,
    title: "IA solidaire",
    text: "Des outils pour structurer une situation, préparer un texte et gagner du temps sur la forme.",
  },
  {
    icon: Scale,
    title: "Défense des droits",
    text: "Un cadre sérieux pour mieux formuler une réclamation et identifier les options à explorer.",
  },
];

const examples = [
  {
    icon: FileText,
    title: "Générer un courrier",
    text: "Transformer une situation confuse en courrier clair, respectueux et personnalisable.",
  },
  {
    icon: ClipboardList,
    title: "Préparer un dossier",
    text: "Lister les pièces utiles, les dates importantes et les éléments à vérifier.",
  },
  {
    icon: Search,
    title: "Analyser une situation",
    text: "Identifier les points de blocage, les urgences et les prochaines étapes possibles.",
  },
  {
    icon: MapPin,
    title: "Orienter",
    text: "Aider à repérer le type d'interlocuteur adapté : association, service public, bailleur, médiateur.",
  },
  {
    icon: MessageSquare,
    title: "Formuler une réclamation",
    text: "Rendre une demande plus précise, factuelle et facile à traiter.",
  },
];

const Features = () => {
  return (
    <section className="features-section" id="outils-nms">
      <div className="features-container">
        <div className="features-header">
          <p className="features-kicker">Les piliers NMS06</p>
          <h2 className="features-title">
            Une plateforme pensée pour agir avec méthode.
          </h2>
          <p className="features-subtitle">
            L'accueil doit rassurer dès les premières secondes : des démarches
            lisibles, une posture humaine, et des outils numériques au service
            des personnes.
          </p>
        </div>

        <div className="features-grid" aria-label="Piliers du projet">
          {pillars.map((pillar) => (
            <article className="feature-card" key={pillar.title}>
              <div className="feature-icon">
                <pillar.icon size={26} />
              </div>
              <h3>{pillar.title}</h3>
              <p>{pillar.text}</p>
            </article>
          ))}
        </div>

        <div className="support-section">
          <div className="support-copy">
            <p className="features-kicker">Ce que NMS06 peut faire pour vous</p>
            <h2>Passer d'un problème flou à une démarche claire.</h2>
            <p>
              Les textes ci-dessous sont volontairement génériques et
              modifiables. Ils décrivent les usages concrets de la plateforme
              sans promettre un résultat juridique automatique.
            </p>
            <Link to="/letter-generator" className="support-link">
              Essayer le générateur de courrier
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="support-grid" aria-label="Exemples d'aide">
            {examples.map((example) => (
              <article className="support-card" key={example.title}>
                <example.icon size={23} />
                <div>
                  <h3>{example.title}</h3>
                  <p>{example.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="human-ai-section">
          <div className="human-ai-copy">
            <p className="features-kicker">Humain + IA</p>
            <h2>L'IA structure. L'humain garde la décision.</h2>
            <p>
              L'IA peut aider à reformuler, classer les informations et préparer
              un document. Elle ne remplace pas l'écoute, la médiation, ni le
              discernement humain nécessaire dans les situations sensibles.
            </p>
          </div>

          <div className="human-ai-grid">
            <div className="human-ai-card">
              <Bot size={28} />
              <h3>L'IA aide à clarifier</h3>
              <p>
                Elle propose une structure, repère les éléments manquants et
                facilite la rédaction.
              </p>
            </div>
            <div className="human-ai-card human-ai-card--warm">
              <ShieldCheck size={28} />
              <h3>L'humain reste au centre</h3>
              <p>
                Chaque situation garde son contexte, son histoire et ses
                conséquences concrètes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
